import { useEffect, useRef } from "react";

/**
 * ASCII/dot-matrix hero backdrop.
 * A grid of tiny dots + occasional glyphs whose visibility is driven by a
 * layered sine-wave flow field, masked so the headline column reads clearly.
 * Brightest cells pick up a teal accent. Auto-pauses when off-screen,
 * disables animation under prefers-reduced-motion.
 */
export default function HeroBackdrop() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const CELL = 12;          // px between dots
    const GLYPHS = ["+", "·", "·", "·", "·", "/", "—", ":"];

    let W = 0, H = 0, cols = 0, rows = 0;
    let glyphMap = [];        // deterministic per-cell glyph index
    let active = true;
    let raf = 0;
    let started = 0;

    function resize() {
      const rect = wrap.getBoundingClientRect();
      W = Math.max(1, Math.floor(rect.width));
      H = Math.max(1, Math.floor(rect.height));
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.floor(W / CELL);
      rows = Math.floor(H / CELL);
      // build glyph map (deterministic, won't change while resized)
      glyphMap = new Array(cols * rows);
      for (let k = 0; k < glyphMap.length; k++) {
        // pseudo-random but stable per index
        const r = (Math.sin(k * 12.9898) * 43758.5453) % 1;
        glyphMap[k] = Math.floor(Math.abs(r) * GLYPHS.length);
      }
    }
    resize();

    const onResize = () => resize();
    window.addEventListener("resize", onResize, { passive: true });

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { active = e.isIntersecting; }),
      { threshold: 0 }
    );
    io.observe(wrap);

    function frame(now) {
      raf = requestAnimationFrame(frame);
      if (!active) return;
      if (!started) started = now;
      const t = (now - started) / 1000;

      ctx.clearRect(0, 0, W, H);
      ctx.font = "10px ui-monospace, 'JetBrains Mono', Menlo, monospace";
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";

      const offX = (W - cols * CELL) / 2 + CELL / 2;
      const offY = (H - rows * CELL) / 2 + CELL / 2;

      // Reading "shadow" — fade cells where the headline sits (left column)
      const cx = W * 0.26;
      const cy = H * 0.5;
      const readR = Math.min(W, H) * 0.55;

      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const x = offX + i * CELL;
          const y = offY + j * CELL;

          // layered flow field
          const a = Math.sin(i * 0.16 + t * 0.65);
          const b = Math.cos(j * 0.13 - t * 0.42);
          const c = Math.sin((i + j) * 0.085 + t * 0.31);
          let v = (a + b + c) / 3;          // -1..1
          v = v * 0.5 + 0.5;                // 0..1

          // dim the area around the text column
          const dx = x - cx;
          const dy = y - cy;
          const d  = Math.sqrt(dx * dx + dy * dy);
          const readFade = Math.min(1, Math.max(0.15, d / readR));
          v *= readFade;

          if (v < 0.42) continue;

          const intensity = (v - 0.42) / 0.58; // 0..1

          if (v > 0.82) {
            // teal accent
            const alpha = 0.30 + intensity * 0.50;
            ctx.fillStyle = `rgba(0, 180, 166, ${alpha.toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(x, y, 1.4, 0, Math.PI * 2);
            ctx.fill();
          } else if (v > 0.70) {
            // brighter ink glyph
            const alpha = 0.20 + intensity * 0.35;
            ctx.fillStyle = `rgba(15, 15, 26, ${alpha.toFixed(3)})`;
            const g = GLYPHS[glyphMap[j * cols + i] || 0];
            ctx.fillText(g, x, y);
          } else {
            // very faint dot
            const alpha = 0.08 + intensity * 0.18;
            ctx.fillStyle = `rgba(15, 15, 26, ${alpha.toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(x, y, 0.7, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    if (reduceMotion) {
      // render a single static frame
      requestAnimationFrame((n) => { active = true; frame(n); cancelAnimationFrame(raf); });
    } else {
      raf = requestAnimationFrame(frame);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      io.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} className="hero-backdrop" aria-hidden="true">
      <canvas ref={canvasRef} className="hb-canvas" />
      <div className="hb-tint" />
      <div className="hb-vignette" />
    </div>
  );
}
